import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, FormError, FormSuccess } from "@/components/FormField";
import { t } from "@/lib/i18n";

export default function SignInPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("signed_out_reason") === "idle") {
      setNotice(t("auth.idleSignedOut"));
      sessionStorage.removeItem("signed_out_reason");
    }
  }, []);

  useEffect(() => {
    if (user) navigate(location.state?.from || (user.is_staff ? "/staff" : "/portal"), { replace: true });
  }, [user, navigate, location.state]);

  const finish = (u) => {
    setUser(u);
    navigate(location.state?.from || (u.is_staff ? "/staff" : "/portal"), { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.status === "mfa_required") setMfaToken(data.mfa_token);
      else finish(data.user);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/challenge", { mfa_token: mfaToken, code });
      finish(data.user);
    } catch (err) {
      setError(apiError(err));
      if (err.response?.status === 401 && /timed out/i.test(apiError(err))) setMfaToken("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16" data-testid="sign-in-page">
      <div className="max-w-md">
        {!mfaToken ? (
          <>
            <h1 className="text-4xl sm:text-5xl">{t("auth.signInTitle")}</h1>
            <form onSubmit={submit} noValidate className="mt-10" data-testid="sign-in-form">
              <FormSuccess message={notice} testId="sign-in-notice" />
              <FormError message={error} />
              <Field label={t("auth.email")} required>
                {(p) => <input {...p} type="email" className="input" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sign-in-email" />}
              </Field>
              <Field label={t("auth.password")} required>
                {(p) => <input {...p} type="password" className="input" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sign-in-password" />}
              </Field>
              <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="sign-in-submit">{busy ? t("common.loading") : t("auth.signIn")}</button>
              <p className="mt-6"><Link to="/forgot-password" className="link" data-testid="sign-in-forgot-link">{t("auth.forgot")}</Link></p>
              <p className="mt-3 text-ink-500">{t("auth.noAccount")} <Link to="/sign-up" className="link" data-testid="sign-in-to-sign-up">{t("auth.signUpTitle")}</Link></p>
            </form>
          </>
        ) : (
          <>
            <h1 className="text-4xl sm:text-5xl">{t("auth.mfaTitle")}</h1>
            <p className="mt-4 text-ink-500">{t("auth.mfaIntro")}</p>
            <form onSubmit={submitMfa} noValidate className="mt-10" data-testid="mfa-form">
              <FormError message={error} />
              <Field label={t("auth.mfaCode")} required>
                {(p) => <input {...p} inputMode="numeric" autoComplete="one-time-code" className="input text-2xl tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-code-input" />}
              </Field>
              <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="mfa-submit">{t("auth.verify")}</button>
              <button type="button" className="btn-quiet mt-4" onClick={() => { setMfaToken(""); setCode(""); setError(""); }} data-testid="mfa-back">{t("common.back")}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
